import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from './actionCreators';
import { WebAuthnStatus } from './actionCreators';
import PageWebAuthnRegisterBody from './PageWebAuthnRegisterBody';
import { useAuth } from '../hooks';
import { DialogTitleWithClose } from '../components';
import { getLabelFromJspVariables } from '../common/JspUtil';
import Button from '@mui/material/Button';
import MuiDialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

/**
 * Self-contained Setup Passkey dialog. Owns its own form state and registers
 * passkeys via Redux dispatches. Notifies the parent via `onDeviceRegistered`
 * after the WebAuthn registration succeeds (parent typically refreshes its
 * passkey list).
 */
const SetupPasskeyDialog = ({ open, onClose, onSetAsDefault, onDeviceRegistered }) => {
    const dispatch = useDispatch();
    const { authenticatedUser } = useAuth();
    const webAuthnRegistration = useSelector(state => state.tfaReducer.tfaStatus.webAuthnRegistration);

    const [deviceName, setDeviceName] = useState('');
    const [startReg, setStartReg] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});

    // Reset on close so reopening starts fresh
    useEffect(() => {
        if (!open) {
            setDeviceName('');
            setStartReg(false);
            setFieldErrors({});
        }
    }, [open]);

    // Notify parent when registration succeeds (so it can refresh its list)
    useEffect(() => {
        if (open && webAuthnRegistration === WebAuthnStatus.SUCCESS && onDeviceRegistered) {
            onDeviceRegistered();
        }
    }, [webAuthnRegistration, open, onDeviceRegistered]);

    const onDeviceNameChange = useCallback((val) => {
        setDeviceName(val);
        if (fieldErrors.deviceName) setFieldErrors({});
    }, [fieldErrors.deviceName]);

    const startRegistration = useCallback(() => {
        if (!deviceName || !deviceName.trim()) {
            setFieldErrors({ deviceName: 'Device name is required' });
            return;
        }
        setFieldErrors({});
        setStartReg(true);
        dispatch(actions.getWebAuthnStartRegistrationRequest(
            authenticatedUser.authenticatedApp,
            authenticatedUser.authenticatedUser,
            deviceName,
        ));
    }, [deviceName, authenticatedUser, dispatch]);

    const registerAnother = useCallback(() => {
        setDeviceName('');
        setStartReg(false);
        setFieldErrors({});
        dispatch({ type: actions.TFA_UPDATE_STATUS, data: { webAuthnRegistration: WebAuthnStatus.IDLE } });
    }, [dispatch]);

    return (
        <MuiDialog open={open} onClose={onClose} maxWidth="md" fullWidth
            aria-labelledby="tfa-setup-passkey-title"
            aria-describedby="tfa-setup-passkey-description">
            <DialogTitleWithClose id="tfa-setup-passkey-title" onClose={onClose} dataAutomationId="tfa_setup_passkey_title">
                {getLabelFromJspVariables('title_setup_passkey')}
            </DialogTitleWithClose>
            <DialogContent id="tfa-setup-passkey-description" sx={{ overflow: 'auto', padding: '17px' }}>
                <PageWebAuthnRegisterBody
                    deviceName={deviceName}
                    onDeviceNameChange={onDeviceNameChange}
                    fieldErrors={fieldErrors}
                    startRegistration={startReg}
                />
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}
                    data-automation-id="tfa_setup_passkey_close_btn">{getLabelFromJspVariables('action_close')}</Button>
                {!startReg &&
                    <Button variant="contained"
                        onClick={startRegistration}
                        data-automation-id="webauthn_register_next_btn">
                        {getLabelFromJspVariables('action_next')}
                    </Button>
                }
                {startReg && webAuthnRegistration === WebAuthnStatus.SUCCESS && <>
                    <Button variant="outlined"
                        onClick={registerAnother}
                        data-automation-id="webauthn_register_another_btn">
                        {getLabelFromJspVariables('action_register_another')}
                    </Button>
                    {onSetAsDefault &&
                        <Button variant="contained"
                            onClick={onSetAsDefault}
                            data-automation-id="webauthn_register_set_default_btn">
                            {getLabelFromJspVariables('action_set_passkey_default')}
                        </Button>
                    }
                </>}
            </DialogActions>
        </MuiDialog>
    );
};

export default SetupPasskeyDialog;
